/**
 * TaskLabelsPicker — add, remove and create a task's labels (FRIDAY-71).
 *
 * Offers the labels exposed on the task's board (the board snapshot only
 * carries those). A label the task already has stays listed even when it was
 * later limited to other boards, so it can still be removed. Saving sends the
 * whole set as `labelNames`; a new name creates a workspace label.
 */

import type { Label, TaskLabelInline } from '@friday/shared/realtime'
import { MultiSelectMenu } from '@/components/ui/MultiSelectMenu'
import { LabelChips } from '@/components/ui/LabelChips'

export function TaskLabelsPicker({
  value,
  boardLabels,
  onChange,
  disabled,
}: {
  value: readonly TaskLabelInline[]
  boardLabels: readonly Label[]
  onChange: (labelNames: string[]) => void
  disabled?: boolean
}) {
  const byId = new Map<string, { id: string; name: string; color: string }>()
  for (const l of boardLabels) byId.set(l.id, l)
  for (const l of value) if (!byId.has(l.id)) byId.set(l.id, l)
  const options = [...byId.values()]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    .map((l) => ({ id: l.id, label: l.name, color: l.color }))
  const names = (ids: string[]) => ids.map((id) => byId.get(id)?.name).filter((n): n is string => !!n)

  return (
    <MultiSelectMenu
      label="Labels"
      options={options}
      value={value.map((l) => l.id)}
      onChange={(ids) => onChange(names(ids))}
      onCreate={(name) => onChange([...value.map((l) => l.name), name])}
      searchable
      disabled={disabled}
      emptyText="No labels on this board yet. Type a name to create one."
      triggerClassName="border-dashed"
      trigger={<LabelChips labels={value} max={4} empty="Add labels…" />}
    />
  )
}
